<template>
    <admin-layout>
        <div class="businesses-page">
            <!-- Reusable Page Header Component -->
            <page-header title="My Businesses" subtitle="Manage and track all your business applications" :breadcrumbs="[
                { label: 'Dashboard', path: '/' },
                { label: 'Businesses', path: '/businesses' }
            ]" :action="{
                label: 'Add New Business',
                icon: 'plus-lg',
                handler: addNewBusiness
            }" />

            <!-- Main Content -->
            <div class="page-content">
                <!-- Search and Filter Section -->
                <div class="search-section">
                    <div class="search-container">
                        <i class="bi bi-search"></i>
                        <input v-model="searchQuery" type="text"
                            placeholder="Search businesses by name, type, or status..." class="search-input"
                            @input="searchBusinesses" />
                        <div v-if="searchQuery" class="search-clear" @click="clearSearch">
                            <i class="bi bi-x-circle"></i>
                        </div>
                    </div>

                    <div class="filter-options">
                        <select v-model="statusFilter" class="filter-select" @change="filterBusinesses">
                            <option value="">All Status</option>
                            <option value="active">Active</option>
                            <option value="pending">Pending</option>
                            <option value="checking">Checking</option>
                            <option value="for payment">For Payment</option>
                            <option value="closed">Closed</option>
                        </select>

                        <select v-model="sortBy" class="filter-select" @change="sortBusinesses">
                            <option value="name">Sort by Name</option>
                            <option value="date">Sort by Date</option>
                            <option value="status">Sort by Status</option>
                        </select>
                    </div>
                </div>

                <!-- Statistics Cards -->
                <div class="stats-container">
                    <stats-card title="Total Businesses" :value="totalBusinesses" icon="building" color="#b71c1c"
                        trend="+2 this month" />
                    <stats-card title="Active Licenses" :value="activeLicenses" icon="check-circle" color="#2e7d32"
                        trend="98% compliance" />
                    <stats-card title="Pending Applications" :value="pendingApplications" icon="clock" color="#f57c00"
                        trend="+3 awaiting" />
                    <stats-card title="Checking" :value="checkings" icon="calendar-exclamation" color="#d32f2f"
                        trend="Next 30 days" />
                </div>

                <!-- Businesses Grid / Empty State -->
                <div v-if="filteredBusinesses.length > 0" class="businesses-grid">
                    <business-card v-for="business in filteredBusinesses" :key="business.id" :business="business"
                        @view="viewBusiness" @renew="renewBusiness" @edit="manageBusiness" />
                </div>

                <!-- Empty State -->
                <div v-else class="empty-state">
                    <div class="empty-state-content">
                        <div class="empty-state-icon">
                            <i class="bi bi-building"></i>
                        </div>
                        <h3>No Businesses Found</h3>
                        <p v-if="searchQuery">No businesses match your search criteria. Try different keywords.</p>
                        <p v-else>You haven't registered any businesses yet. Start by adding your first business.</p>
                        <div class="empty-state-actions">
                            <button class="btn btn-primary" @click="addNewBusiness">
                                <i class="bi bi-plus-lg"></i>
                                Register New Business
                            </button>
                            <button v-if="searchQuery" class="btn btn-outline" @click="clearSearch">
                                <i class="bi bi-x-circle"></i>
                                Clear Search
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Pagination -->
                <div v-if="filteredBusinesses.length > 0" class="pagination">
                    <button class="pagination-btn" :disabled="currentPage === 1" @click="prevPage">
                        <i class="bi bi-chevron-left"></i>
                    </button>
                    <span class="pagination-info">
                        Page {{ currentPage }} of {{ totalPages }}
                    </span>
                    <button class="pagination-btn" :disabled="currentPage === totalPages" @click="nextPage">
                        <i class="bi bi-chevron-right"></i>
                    </button>
                </div>
            </div>

            <!-- Add Business Modal -->
            <fullscreen-modal :show="showAddModal" @close="showAddModal = false" @saved="handleBusinessAdded">
                <business-info-viewer @back="toggleModal" :show="mode === 'view'" v-if="mode === 'view'" ref="busInfoRef" />
                <business-info @save-success="handleSaveSuccess" @close="toggleModal" :show="mode === 'add-edit'" v-if="mode === 'add-edit'" ref="busRef" />
            </fullscreen-modal>
        </div>
    </admin-layout>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import PageHeader from '@/components/commons/PageHeader.vue';
import StatsCard from '@/components/commons/StatsCard.vue';
import FullscreenModal from '@/components/modals/FullscreenModal.vue';
import BusinessCard from '@/components/commons/BusinessCard.vue';
import AdminLayout from '@/layouts/AdminLayout.vue';
import businessService from '@/services/businessService';
import spinnerService from '@/services/spinnerService';
import BusinessInfoViewer from '@/components/infos/BusinessInfoViewer.vue';
import BusinessInfo from '@/components/infos/BusinessInfo.vue';
import toastService from '@/services/toastService';

// Mock data - replace with actual API calls
//eslint-disable-next-line
const mockBusinesses = [
    {
        id: 1,
        name: "Tech Innovations Inc.",
        type: "IT Services",
        licenseNumber: "BPL-2024-00123",
        status: "active",
        expiryDate: "2024-12-31",
        address: "123 Tech Street, Metro City",
        owner: "John Smith",
        employees: 25,
        revenue: "₱5,000,000",
        lastInspection: "2024-01-15",
        nextRenewal: "2024-10-01",
        category: "Professional Services",
        complianceScore: 95,
        documents: 5,
        logo: "/api/placeholder/300/200"
    },
    {
        id: 2,
        name: "Green Leaf Restaurant",
        type: "Food Service",
        licenseNumber: "BPL-2024-00245",
        status: "pending",
        expiryDate: "2024-06-30",
        address: "456 Food Ave, District 2",
        owner: "Maria Garcia",
        employees: 15,
        revenue: "₱3,200,000",
        lastInspection: "2024-02-10",
        nextRenewal: "2024-04-15",
        category: "Restaurant",
        complianceScore: 88,
        documents: 3,
        logo: "/api/placeholder/300/200"
    },
    {
        id: 3,
        name: "Urban Clothing Co.",
        type: "Retail",
        licenseNumber: "BPL-2023-00987",
        status: "renewal",
        expiryDate: "2024-03-31",
        address: "789 Fashion Blvd, Commercial Zone",
        owner: "Robert Chen",
        employees: 8,
        revenue: "₱2,500,000",
        lastInspection: "2023-11-20",
        nextRenewal: "2024-03-01",
        category: "Retail",
        complianceScore: 92,
        documents: 4,
        logo: "/api/placeholder/300/200"
    },
    {
        id: 4,
        name: "Clean Water Solutions",
        type: "Environmental Services",
        licenseNumber: "BPL-2024-00321",
        status: "active",
        expiryDate: "2025-01-15",
        address: "321 Eco Road, Green District",
        owner: "Sarah Johnson",
        employees: 12,
        revenue: "₱4,100,000",
        lastInspection: "2024-01-30",
        nextRenewal: "2024-11-01",
        category: "Environmental",
        complianceScore: 98,
        documents: 6,
        logo: "/api/placeholder/300/200"
    },
    {
        id: 5,
        name: "City Transport Services",
        type: "Transportation",
        licenseNumber: "BPL-2023-01542",
        status: "expired",
        expiryDate: "2023-12-15",
        address: "654 Transport Way, Industrial Area",
        owner: "Michael Brown",
        employees: 35,
        revenue: "₱8,700,000",
        lastInspection: "2023-09-05",
        nextRenewal: "2024-01-01",
        category: "Transportation",
        complianceScore: 76,
        documents: 2,
        logo: "/api/placeholder/300/200"
    }
];
const handleSaveSuccess = ()=>{
    toggleModal();
    getLinkedAccounts();
}
const mode = ref("add-edit");
const busRef = ref(null);

// Reactive state
const searchQuery = ref('');
const statusFilter = ref('');
const sortBy = ref('name');
const currentPage = ref(1);
const itemsPerPage = 12;
const showAddModal = ref(false);
const businesses = ref([]);
const busInfoRef = ref(null);
// Computed properties
const filteredBusinesses = computed(() => {
    let filtered = businesses.value;

    // Apply search filter
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter(business =>
            business.business_name.toLowerCase().includes(query) ||
            business.application_type.toLowerCase().includes(query) ||
            business.bin.toLowerCase().includes(query) ||
            business.status.toLowerCase().includes(query)
        );
    }

    // Apply status filter
    if (statusFilter.value) {
        filtered = filtered.filter(business => business.status?.toLowerCase() === statusFilter.value.toLowerCase());
    }

    // Apply sorting
    filtered.sort((a, b) => {
        switch (sortBy.value) {
            case 'date':
                return new Date(b.expiryDate) - new Date(a.expiryDate);
            case 'status':
                return a.status.localeCompare(b.status);
            default:
                return a.business_name.localeCompare(b.business_name);
        }
    });

    // Apply pagination
    const start = (currentPage.value - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filtered.slice(start, end);
});

const toggleModal = () => {
    showAddModal.value = !showAddModal.value;
}

const totalBusinesses = computed(() => businesses.value.length);
const activeLicenses = computed(() => businesses.value.filter(b => b.status?.toLowerCase() === 'active').length);
const pendingApplications = computed(() => businesses.value.filter(b => b.status?.toLowerCase() === 'pending').length);
const checkings = computed(() => businesses.value.filter(b =>
    b.status?.toLowerCase() === 'checking'
).length);

const totalPages = computed(() => Math.ceil(businesses.value.length / itemsPerPage));

// Methods
const searchBusinesses = () => {
    currentPage.value = 1; // Reset to first page when searching
};

const clearSearch = () => {
    searchQuery.value = '';
    statusFilter.value = '';
    searchBusinesses();
};

const filterBusinesses = () => {
    currentPage.value = 1;
};

const sortBusinesses = () => {
    currentPage.value = 1;
};

const prevPage = () => {
    if (currentPage.value > 1) currentPage.value--;
};

const nextPage = () => {
    if (currentPage.value < totalPages.value) currentPage.value++;
};

const addNewBusiness = () => {
    mode.value = "add-edit";
    nextTick(() => {
        if (busRef.value)
            busRef.value.intitalizeLoadedData()
    });
    showAddModal.value = true;
};

const handleBusinessAdded = (newBusiness) => {
    businesses.value.unshift(newBusiness);
    showAddModal.value = false;
};

const viewBusiness = async (business) => {
    // Navigate to business detail page
    mode.value = 'view';
    toggleModal();
    nextTick(() => {
        busInfoRef.value.getBusinessInfo(business.application_id);
    })
    console.log('View business:', business);
    // router.push(`/businesses/${business.id}`);
};

const canRenew = (business) => {
    const currentYear = new Date().getFullYear().toString(); // "2024"
    
    if(business.year < currentYear && business.renewal_status?.toLowerCase() === "current") {
        // Business already renewed for current year
        return true;
    }
    
    return true;
};


const renewBusiness = (business) => {
    // Handle renewal
    if(!canRenew(business))
        return;
    mode.value = 'add-edit';
    toggleModal();
    nextTick(()=>{
        busRef.value.getBusinessInfoForRenewal(business.application_id)
    })
};

const manageBusiness = (business) => {
    if(business.status?.toLowerCase() !== 'pending'){
        toastService.error("You are only allowed to edit business information while the status is still pending.")
        return;
    }
    mode.value = "add-edit";
    toggleModal();
    nextTick(()=>{
        busRef.value.getBusinessInfo(business.application_id);
    });
    // Navigate to management page
    // console.log('Manage business:', business);
    // router.push(`/businesses/${business.id}/manage`);

};



const getLinkedAccounts = async () => {
    spinnerService.show();
    businesses.value = await businessService.getLinkedAccounts();
    spinnerService.hide();
}

// Lifecycle
onMounted(() => {
    // Load businesses from API
    //businesses.value = mockBusinesses;
    getLinkedAccounts();
});
</script>

<style scoped>
.businesses-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%);
}

.page-content {
    padding: 2rem;
    max-width: 1400px;
    margin: 0 auto;
}

/* Search Section */
.search-section {
    background: white;
    border-radius: 16px;
    padding: 1.5rem;
    margin-bottom: 2rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    border: 1px solid #eaeaea;
}

.search-container {
    position: relative;
    margin-bottom: 1rem;
}

.search-container i {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
    font-size: 1.1rem;
}

.search-input {
    width: 100%;
    padding: 0.875rem 1rem 0.875rem 3rem;
    border: 2px solid #e0e0e0;
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.3s ease;
    background: #fafafa;
}

.search-input:focus {
    outline: none;
    border-color: #b71c1c;
    background: white;
    box-shadow: 0 0 0 3px rgba(183, 28, 28, 0.1);
}

.search-clear {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    cursor: pointer;
    transition: color 0.3s ease;
}

.search-clear:hover {
    color: #b71c1c;
}

/* Filter Options */
.filter-options {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
}

.filter-select {
    padding: 0.75rem 1rem;
    border: 2px solid #e0e0e0;
    border-radius: 10px;
    background: white;
    font-size: 0.9rem;
    color: #333;
    cursor: pointer;
    transition: all 0.3s ease;
    min-width: 150px;
}

.filter-select:focus {
    outline: none;
    border-color: #b71c1c;
    box-shadow: 0 0 0 3px rgba(183, 28, 28, 0.1);
}

/* Statistics Container */
.stats-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
}

/* Businesses Grid */
.businesses-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
}

/* Responsive Design */
@media (max-width: 768px) {
    .businesses-grid {
        grid-template-columns: 1fr !important;
        /* Force single column on mobile */
    }
}

@media (max-width: 1024px) and (min-width: 769px) {
    .businesses-grid {
        grid-template-columns: repeat(2, 1fr);
        /* Two columns on tablets */
    }
}

@media (min-width: 1025px) {
    .businesses-grid {
        grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
        /* Auto columns on desktop */
    }
}

/* Empty State */
.empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    background: white;
    border-radius: 20px;
    border: 2px dashed #e0e0e0;
    margin: 2rem 0;
}

.empty-state-content {
    text-align: center;
    padding: 3rem;
    max-width: 400px;
}

.empty-state-icon {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, rgba(183, 28, 28, 0.1), rgba(183, 28, 28, 0.05));
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    color: #b71c1c;
    font-size: 2.5rem;
}

.empty-state h3 {
    font-size: 1.5rem;
    color: #333;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.empty-state p {
    color: #666;
    margin-bottom: 1.5rem;
    line-height: 1.5;
}

.empty-state-actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
}

/* Pagination */
.pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 1.5rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.pagination-btn {
    width: 40px;
    height: 40px;
    border: 2px solid #e0e0e0;
    border-radius: 10px;
    background: white;
    color: #666;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.pagination-btn:hover:not(:disabled) {
    border-color: #b71c1c;
    color: #b71c1c;
    transform: translateY(-2px);
}

.pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.pagination-info {
    color: #666;
    font-weight: 500;
    min-width: 120px;
    text-align: center;
}

/* Buttons */
.btn {
    padding: 0.75rem 1.5rem;
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    border: none;
}

.btn-primary {
    background: linear-gradient(135deg, #b71c1c, #d32f2f);
    color: white;
    box-shadow: 0 4px 15px rgba(183, 28, 28, 0.2);
}

.btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(183, 28, 28, 0.3);
}

.btn-outline {
    background: transparent;
    border: 2px solid #e0e0e0;
    color: #666;
}

.btn-outline:hover {
    border-color: #b71c1c;
    color: #b71c1c;
}

/* Responsive Design */
@media (max-width: 768px) {
    .page-content {
        padding: 1rem;
    }

    .businesses-grid {
        grid-template-columns: 1fr;
    }

    .stats-container {
        grid-template-columns: 1fr;
    }

    .filter-options {
        flex-direction: column;
    }

    .filter-select {
        width: 100%;
    }

    .empty-state-actions {
        flex-direction: column;
    }

    .empty-state-content {
        padding: 2rem 1rem;
    }
}

@media (max-width: 1024px) {
    .businesses-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

/* Animations */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.businesses-grid>* {
    animation: fadeIn 0.5s ease-out;
}
</style>